import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Puzzle, Hash, Calculator, Eye } from 'lucide-react';
import { SpectatorMap } from '@/components/map';
import { StickyFooter, HuntDeletedModal, VictoryCelebration, VictoryHeader } from '@/components/common';
import { ParticipantDrawer } from './ParticipantDrawer';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useSignalR, ItemFoundMessage, PinDiscoveredMessage, ParticipantRemovedMessage, HuntDeletedMessage, HuntCompletedMessage, ChallengeEncounteredMessage, ChallengeAttemptMessage, ChallengeSolvedMessage, ValidationRequestedMessage, ValidationApprovedMessage, ValidationRejectedMessage } from '@/hooks/useSignalR';
import { useLocationStore, useHunterLocations } from '@/stores/locationStore';
import { useAuthStore } from '@/stores/authStore';
import { useHuntStore } from '@/stores/huntStore';
import { huntService } from '@/services/api/huntService';
import { pinService } from '@/services/api/pinService';
import { validationService, PendingValidation } from '@/services/api/validationService';
import { Pin, ParticipantSummary } from '@gps-hunt/shared';

// Local type definition
type ChallengeType = 'riddle' | 'hex_code' | 'numerical';

const AZURE_MAPS_KEY = import.meta.env.VITE_AZURE_MAPS_KEY || '';
const LOCATION_UPDATE_INTERVAL = 3000;

interface HuntProgress {
  currentPinIndex: number;
  completedPinIds: string[];
  itemsFoundAtCurrentPin: number;
}

/**
 * Active hunt view for hiders - spectator map showing all participants and pins
 */
export const HiderActiveView: React.FC = () => {
  const { huntId } = useParams<{ huntId: string }>();
  const navigate = useNavigate();
  
  const { position, startWatching, stopWatching, isWatching } = useGeolocation();
  const { setMyLocation, clearLocations, removeHunter } = useLocationStore();
  const participantLocations = useHunterLocations();

  const [pins, setPins] = useState<Pin[]>([]);
  const [progress, setProgress] = useState<HuntProgress>({
    currentPinIndex: 0,
    completedPinIds: [],
    itemsFoundAtCurrentPin: 0,
  });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [huntInfo, setHuntInfo] = useState<{ createdBy: string; participants: ParticipantSummary[] } | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [huntDeletedInfo, setHuntDeletedInfo] = useState<{ huntName: string; message: string } | null>(null);
  const [huntComplete, setHuntComplete] = useState(false);
  const [completionTime, setCompletionTime] = useState<string | null>(null);
  const hasLoadedOnce = useRef(false);
  const pinsRef = useRef<Pin[]>([]);
  const huntCompleteRef = useRef(false);
  const wasConnectedRef = useRef(false); // Track for reconnection logic
  const fetchDataRef = useRef<(() => Promise<void>) | null>(null); // Stable fetch ref

  // Keep huntCompleteRef in sync
  useEffect(() => {
    huntCompleteRef.current = huntComplete;
  }, [huntComplete]);

  // Challenge spectating state
  const [activeChallenge, setActiveChallenge] = useState<{
    pinId: string;
    pinIndex: number;
    challengeType: ChallengeType;
    challengeQuestion: string;
    captainDisplayName: string;
    answer?: string; // Hider can see the answer
  } | null>(null);
  const [challengeAttempts, setChallengeAttempts] = useState<Array<{
    answer: string;
    isCorrect: boolean;
    timestamp: string;
  }>>([]);
  
  // Referee state
  const [isDecrementing, setIsDecrementing] = useState(false);
  const [pendingValidation, setPendingValidation] = useState<PendingValidation | null>(null);
  const [isValidating, setIsValidating] = useState(false);
    // Get current user
  const currentUser = useAuthStore((state) => state.user);
  const resetHuntStore = useHuntStore((state) => state.reset);

  // Keep pinsRef in sync
  useEffect(() => {
    pinsRef.current = pins;
  }, [pins]);

  // Handle item found events from hunters
  const handleItemFound = useCallback((msg: ItemFoundMessage) => {
    setProgress(prev => {
      // Get current pin to check if this item belongs to it
      const currentPin = pinsRef.current[prev.currentPinIndex];
      if (currentPin && msg.pinId === currentPin.id) {
        return {
          ...prev,
          itemsFoundAtCurrentPin: msg.totalFound,
        };
      }
      return prev;
    });
  }, []);

  // Handle pin discovered/completed (called when hunters complete a pin)
  const handlePinDiscovered = useCallback((msg: PinDiscoveredMessage) => {
    setProgress(prev => {
      // Only advance if this pin isn't already completed
      if (prev.completedPinIds.includes(msg.pinId)) {
        return prev;
      }
      
      const nextPinIndex = msg.pinIndex + 1;
      // Clamp to valid range - don't go beyond last pin
      const maxIndex = Math.max(0, pinsRef.current.length - 1);
      const clampedIndex = Math.min(nextPinIndex, maxIndex);
      return {
        ...prev,
        currentPinIndex: clampedIndex,
        completedPinIds: [...prev.completedPinIds, msg.pinId],
        itemsFoundAtCurrentPin: 0, // Reset for next pin
      };
    });
    
    // Clear any active challenge when pin is completed
    setActiveChallenge(null);
    setChallengeAttempts([]);
  }, []);

  // Handle participant removed/kicked during hunt
  const handleParticipantRemoved = useCallback((msg: ParticipantRemovedMessage) => {
    // Remove from local location tracking
    removeHunter(msg.userId);
    
    // Update hunt info participants list
    setHuntInfo(prev => prev ? {
      ...prev,
      participants: prev.participants.filter(p => p.userId !== msg.userId),
    } : null);
    
    // If current user was kicked, redirect to home
    if (currentUser?.uid === msg.userId) {
      const reason = msg.justification 
        ? `\nReason: ${msg.justification}` 
        : '';
      alert(`You have been removed from the hunt.${reason}`);
      navigate('/', { replace: true });
    }
  }, [currentUser?.uid, navigate, removeHunter]);

  // Handle hunt deleted
  const handleHuntDeleted = useCallback((msg: HuntDeletedMessage) => {
    resetHuntStore(); // Clear hunt state immediately to remove join code from header
    setHuntDeletedInfo({ huntName: msg.huntName, message: msg.message });
  }, [resetHuntStore]);

  // Handle challenge encountered - show spectator view for hider
  const handleChallengeEncountered = useCallback((msg: ChallengeEncounteredMessage) => {
    console.log('[HiderActiveView] Challenge encountered:', msg);
    // Get answer from pins (hiders can see answers)
    const pin = pinsRef.current.find(p => p.id === msg.pinId);
    const answer = (pin as { challenge?: { answer: string } })?.challenge?.answer;
    console.log('[HiderActiveView] Setting activeChallenge, pin found:', !!pin, 'answer:', answer);
    
    setActiveChallenge({
      pinId: msg.pinId,
      pinIndex: msg.pinIndex,
      challengeType: msg.challengeType as ChallengeType,
      challengeQuestion: msg.challengeQuestion,
      captainDisplayName: msg.captainDisplayName,
      answer,
    });
    setChallengeAttempts([]);
  }, []);

  // Handle challenge attempt - add to attempts list
  const handleChallengeAttempt = useCallback((msg: ChallengeAttemptMessage) => {
    console.log('[HiderActiveView] Challenge attempt:', msg);
    setChallengeAttempts(prev => [
      ...prev,
      { answer: msg.answer, isCorrect: msg.isCorrect, timestamp: msg.timestamp },
    ]);
  }, []);

  // Handle challenge solved - clear challenge state after delay for celebration
  const handleChallengeSolved = useCallback((msg: ChallengeSolvedMessage) => {
    console.log('[HiderActiveView] Challenge solved:', msg);
    // Delay clearing to show the final successful attempt
    setTimeout(() => {
      setActiveChallenge(null);
      setChallengeAttempts([]);
    }, 3000);
  }, []);

  // Handle hunt completed - show celebration
  const handleHuntCompleted = useCallback((msg: HuntCompletedMessage) => {
    setHuntComplete(true);
    setCompletionTime(msg.completedAt || new Date().toISOString());
  }, []);

  // Handle validation requested - hiders need to validate
  const handleValidationRequested = useCallback((msg: ValidationRequestedMessage) => {
    setPendingValidation({
      pinId: msg.pinId,
      pinIndex: msg.pinIndex,
      requestedBy: msg.requestedBy,
      requestedByDisplayName: msg.requestedByDisplayName,
      requestedAt: msg.requestedAt,
    });
  }, []);

  // Handle validation approved/rejected - clear validation state
  const handleValidationApproved = useCallback((_msg: ValidationApprovedMessage) => {
    setPendingValidation(null);
  }, []);

  const handleValidationRejected = useCallback((_msg: ValidationRejectedMessage) => {
    setPendingValidation(null);
  }, []);

  // SignalR connection with handlers
  const { isConnected, connect, disconnect, sendLocation } = useSignalR({
    onItemFound: handleItemFound,
    onPinDiscovered: handlePinDiscovered,
    onParticipantRemoved: handleParticipantRemoved,
    onHuntDeleted: handleHuntDeleted,
    onHuntCompleted: handleHuntCompleted,
    onChallengeEncountered: handleChallengeEncountered,
    onChallengeAttempt: handleChallengeAttempt,
    onChallengeSolved: handleChallengeSolved,
    onValidationRequested: handleValidationRequested,
    onValidationApproved: handleValidationApproved,
    onValidationRejected: handleValidationRejected,
  });

  // Initialize
  useEffect(() => {
    if (!huntId) return;

    startWatching();
    connect(huntId);

    return () => {
      stopWatching();
      disconnect();
      clearLocations();
    };
  }, [huntId, startWatching, stopWatching, connect, disconnect, clearLocations]);

  // Broadcast my location
  useEffect(() => {
    if (!huntId || !position || !isWatching || !isConnected) return;

    setMyLocation({
      latitude: position.latitude,
      longitude: position.longitude,
      accuracy: position.accuracy,
      heading: position.heading ?? undefined,
      speed: position.speed ?? undefined,
    });

    const interval = setInterval(() => {
      sendLocation(huntId, {
        latitude: position.latitude,
        longitude: position.longitude,
        accuracy: position.accuracy,
        heading: position.heading,
        speed: position.speed,
      });
    }, LOCATION_UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, [huntId, position, isWatching, isConnected, sendLocation, setMyLocation]);

  // Fetch hunt data
  useEffect(() => {
    const fetchData = async () => {
      if (!huntId) return;

      try {
        // Only show loading on initial fetch
        if (!hasLoadedOnce.current) {
          setIsInitialLoading(true);
        }
        
        // Fetch pins and hunt data in parallel
        const [pinsData, huntData, pendingVal] = await Promise.all([
          pinService.getPins(huntId),
          huntService.getHunt(huntId),
          validationService.getPendingValidation(huntId),
        ]);

        // Store full pin data including challenge info for hiders
        const pinArray = pinsData.pins.map((p) => ({
          id: p.id,
          huntId: huntId,
          latitude: p.latitude,
          longitude: p.longitude,
          accuracy: p.accuracy,
          itemCount: p.itemCount,
          hintPhotoUrl: p.hintPhotoUrl,
          sequenceOrder: p.sequenceOrder,
          createdBy: '',
          createdAt: '',
          // Include challenge data for spectating
          challenge: p.challenge,
        })) as Pin[];
        setPins(pinArray);

        // Use progress from API if available
        if (huntData.progress) {
          // Clamp currentPinIndex to valid range (0 to pins.length - 1)
          const maxIndex = Math.max(0, pinArray.length - 1);
          setProgress({
            currentPinIndex: Math.min(huntData.progress.currentPinIndex, maxIndex),
            completedPinIds: huntData.progress.completedPinIds,
            itemsFoundAtCurrentPin: huntData.progress.itemsFoundAtCurrentPin,
          });
        }
        
        // Check if hunt is completed - show celebration (fallback for missed SignalR)
        if (huntData.hunt.status === 'completed' && huntData.hunt.completedAt && !huntCompleteRef.current) {
          setHuntComplete(true);
          setCompletionTime(huntData.hunt.completedAt);
        }
        
        // Update pending validation state
        setPendingValidation(pendingVal);
        
        // Store hunt info for participant management
        setHuntInfo({
          createdBy: huntData.hunt.createdBy,
          participants: huntData.participants,
        });
        
        hasLoadedOnce.current = true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load hunt');
      } finally {
        setIsInitialLoading(false);
      }
    };

    // Store fetch function for reconnection effect
    fetchDataRef.current = fetchData;
    
    fetchData();
    
    // Poll for updates - faster when SignalR is disconnected
    const pollInterval = isConnected ? 5000 : 2000;
    const interval = setInterval(fetchData, pollInterval);
    return () => clearInterval(interval);
  }, [huntId, isConnected]);

  // Handle reconnection - refresh data immediately when SignalR reconnects
  useEffect(() => {
    if (isConnected && !wasConnectedRef.current) {
      // Just reconnected - fetch latest state immediately
      console.log('[HiderActiveView] SignalR reconnected, refreshing state');
      fetchDataRef.current?.();
    }
    wasConnectedRef.current = isConnected;
  }, [isConnected]);

  // Handle view stats button
  const handleViewStats = useCallback(() => {
    navigate(`/stats/${huntId}`, { replace: true });
  }, [huntId, navigate]);

  // Handle decrement item (hider referee action)
  const handleDecrementItem = useCallback(async () => {
    if (!huntId || progress.itemsFoundAtCurrentPin === 0) return;
    
    // Get current pin ID
    const currentPin = pinsRef.current[progress.currentPinIndex];
    if (!currentPin) return;

    setIsDecrementing(true);
    try {
      const result = await pinService.decrementItemFound(huntId, currentPin.id);
      // Update local state immediately
      setProgress(prev => ({
        ...prev,
        itemsFoundAtCurrentPin: result.totalFoundAtPin,
      }));
    } catch (err) {
      console.error('Failed to decrement item:', err);
      setError(err instanceof Error ? err.message : 'Failed to decrement item');
    } finally {
      setIsDecrementing(false);
    }
  }, [huntId, progress.currentPinIndex, progress.itemsFoundAtCurrentPin]);

  // Handle approve validation (hider referee action)
  const handleApproveValidation = useCallback(async () => {
    if (!huntId || !pendingValidation) return;

    setIsValidating(true);
    try {
      await validationService.approveValidation(huntId, pendingValidation.pinId);
      // Validation state will be cleared by SignalR handler
    } catch (err) {
      console.error('Failed to approve validation:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setIsValidating(false);
    }
  }, [huntId, pendingValidation]);

  // Handle reject validation (hider referee action)
  const handleRejectValidation = useCallback(async () => {
    if (!huntId || !pendingValidation) return;

    setIsValidating(true);
    try {
      await validationService.rejectValidation(huntId, pendingValidation.pinId);
      // Validation state will be cleared by SignalR handler
    } catch (err) {
      console.error('Failed to reject validation:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setIsValidating(false);
    }
  }, [huntId, pendingValidation]);

  if (isInitialLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-text-secondary">Loading hunt...</div>
      </div>
    );
  }

  // Show hunt complete celebration for hiders
  if (huntComplete) {
    return (
      <div className="h-full bg-[#0A0A0F] flex flex-col items-center justify-center p-8 relative overflow-hidden">
        <VictoryCelebration />
        
        <VictoryHeader 
          title="HUNT COMPLETE!"
          subtitle="The hunters found all your pins!"
        />
        
        {completionTime && (
          <p className="text-gray-400 text-sm mt-4 z-10">
            Completed at {new Date(completionTime).toLocaleTimeString()}
          </p>
        )}
        
        <button
          onClick={handleViewStats}
          className="mt-8 px-10 py-5 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white text-xl font-bold rounded-xl hover:scale-105 active:scale-95 transition-all shadow-lg shadow-orange-500/50 z-10"
        >
          VIEW STATS →
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  // Get item count for current pin
  const currentPin = pins[progress.currentPinIndex];
  const currentPinItemCount = currentPin?.itemCount || 1;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Map fills available space */}
      <div className="flex-1 relative">
        <SpectatorMap
          subscriptionKey={AZURE_MAPS_KEY}
          pins={pins}
          activePinIndex={progress.currentPinIndex}
          completedPinIds={progress.completedPinIds}
          participants={participantLocations.map(p => ({
            userId: p.userId,
            displayName: p.displayName,
            latitude: p.latitude,
            longitude: p.longitude,
            role: p.role as 'hunter' | 'hider',
          }))}
          userLocation={position ? {
            latitude: position.latitude,
            longitude: position.longitude,
            accuracy: position.accuracy,
          } : undefined}
          center={position ? [position.longitude, position.latitude] : undefined}
          className="w-full h-full"
        />

        {/* Top right controls */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          {/* Participants button */}
          <button
            onClick={() => setIsDrawerOpen(true)}
            className="bg-secondary/90 rounded-lg px-3 py-2 flex items-center gap-2 hover:bg-secondary transition-colors"
          >
            <Users className="w-4 h-4 text-primary" />
            <span className="text-xs text-text-secondary">
              {participantLocations.length + 1}
            </span>
          </button>
        </div>

        {/* Legend */}
        <div className="absolute top-4 left-4 bg-secondary/90 rounded-lg p-3 text-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-text-secondary">Hunters ({participantLocations.filter(p => p.role === 'hunter').length})</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-text-secondary">Hiders ({participantLocations.filter(p => p.role === 'hider').length + 1})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
            <span className="text-text-secondary">Active Pin</span>
          </div>
        </div>

        {/* Challenge spectator panel - shows when challenge is active */}
        {activeChallenge && (
          <div className="absolute top-28 left-4 right-4 max-w-sm bg-secondary/95 rounded-lg p-4 shadow-lg border border-white/10">
            {/* Header with icon */}
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-5 h-5 text-primary" />
              <span className="font-bold text-white">Challenge Active</span>
              {activeChallenge.challengeType === 'riddle' && <Puzzle className="w-4 h-4 text-purple-400" />}
              {activeChallenge.challengeType === 'hex_code' && <Hash className="w-4 h-4 text-cyan-400" />}
              {activeChallenge.challengeType === 'numerical' && <Calculator className="w-4 h-4 text-amber-400" />}
            </div>
            
            {/* Captain info */}
            <div className="text-sm text-text-secondary mb-2">
              Captain: <span className="text-white font-medium">{activeChallenge.captainDisplayName}</span>
            </div>
            
            {/* Question */}
            <div className="text-sm text-text-secondary mb-2">
              Question: <span className="text-white">{activeChallenge.challengeQuestion}</span>
            </div>
            
            {/* Answer (hiders can see) */}
            {activeChallenge.answer && (
              <div className="bg-green-500/20 border border-green-500/40 rounded p-2 mb-3">
                <span className="text-xs text-green-400">Answer: </span>
                <span className="text-green-300 font-mono">{activeChallenge.answer}</span>
              </div>
            )}
            
            {/* Attempts */}
            {challengeAttempts.length > 0 && (
              <div className="mt-2">
                <div className="text-xs text-text-secondary mb-1">Attempts ({challengeAttempts.length}):</div>
                <div className="max-h-24 overflow-y-auto space-y-1">
                  {challengeAttempts.map((attempt, idx) => (
                    <div
                      key={idx}
                      className={`text-xs p-1 rounded ${
                        attempt.isCorrect ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                      }`}
                    >
                      &quot;{attempt.answer}&quot; {attempt.isCorrect ? '✓' : '✗'}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sticky footer with item progress */}
      <StickyFooter>
        <div className="w-full">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-text-secondary">
              Pin {progress.currentPinIndex + 1} of {pins.length || 1} - Items
            </span>
            <span className="text-sm font-mono">
              {progress.itemsFoundAtCurrentPin} / {currentPinItemCount}
            </span>
          </div>
          <div className="flex justify-center gap-3 mb-3">
            {Array.from({ length: currentPinItemCount }, (_, index) => (
              <div
                key={index}
                className={`w-8 h-8 rounded-full border-2 transition-all duration-300 ${
                  index < progress.itemsFoundAtCurrentPin
                    ? 'bg-green-500 border-green-500'
                    : 'bg-transparent border-white/40'
                }`}
              >
                {index < progress.itemsFoundAtCurrentPin && (
                  <svg
                    className="w-full h-full text-white p-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>
          {/* Validation panel - shows when hunters request validation */}
          {pendingValidation ? (
            <div className="space-y-3">
              <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3">
                <div className="flex items-center justify-center gap-2 text-yellow-400 font-medium mb-1">
                  <span className="animate-pulse">⚡</span>
                  Validation Requested
                </div>
                <div className="text-sm text-center text-yellow-200/80">
                  {pendingValidation.requestedByDisplayName} says they found all items
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleRejectValidation}
                  disabled={isValidating}
                  className="flex-1 py-3 px-4 rounded-lg font-medium bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30 active:scale-95 transition-all"
                >
                  {isValidating ? '...' : '✗ ITEM NOT FOUND'}
                </button>
                <button
                  onClick={handleApproveValidation}
                  disabled={isValidating}
                  className="flex-1 py-3 px-4 rounded-lg font-medium bg-green-500/20 border border-green-500/50 text-green-400 hover:bg-green-500/30 active:scale-95 transition-all"
                >
                  {isValidating ? '...' : '✓ VALIDATE'}
                </button>
              </div>
            </div>
          ) : (
            /* Referee button - only enabled when items have been found */
            <button
              onClick={handleDecrementItem}
              disabled={progress.itemsFoundAtCurrentPin === 0 || isDecrementing}
              className={`w-full py-3 px-4 rounded-lg font-medium text-sm transition-all ${
                progress.itemsFoundAtCurrentPin > 0
                  ? 'bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30 active:scale-95'
                  : 'bg-gray-800/50 border border-gray-700/50 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isDecrementing ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⟳</span>
                  Updating...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  ✗ ITEM NOT FOUND (Referee)
                </span>
              )}
            </button>
          )}
        </div>
      </StickyFooter>
      
      {/* Participant management drawer */}
      {huntInfo && currentUser && (
        <ParticipantDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          huntId={huntId || ''}
          participants={[
            // Include self
            ...(huntInfo.participants.map(p => ({
              odifiedUserId: p.userId,
              displayName: p.displayName,
              role: p.role as 'hunter' | 'hider',
              connectionStatus: participantLocations.find(loc => loc.userId === p.userId)?.connectionStatus,
            }))),
          ]}
          currentUserId={currentUser.uid}
          creatorId={huntInfo.createdBy}
          onParticipantKicked={(userId) => {
            // Update local state when participant is kicked
            setHuntInfo(prev => prev ? {
              ...prev,
              participants: prev.participants.filter(p => p.userId !== userId),
            } : null);
          }}
        />
      )}

      {/* Hunt deleted modal */}
      {huntDeletedInfo && (
        <HuntDeletedModal
          huntName={huntDeletedInfo.huntName}
          message={huntDeletedInfo.message}
          onDismiss={() => {
            // State already cleared by signal handler
            navigate('/', { replace: true });
          }}
        />
      )}
    </div>
  );
};

export default HiderActiveView;
